/**
 * Unsharp masking on raw RGBA buffers.
 *
 * Same motivation as `resample.ts`: the sharpening pass used to run on the main
 * thread using `ctx.filter = 'blur(...)'` plus a per-pixel loop. For a 4x upscale
 * that loop touches tens of millions of pixels and visibly freezes the page.
 * Implementing the blur directly keeps it pure, worker-safe and testable.
 *
 * The blur is a separable Gaussian. Previously this was three `drawImage` calls
 * with a CSS `blur()` filter; a real Gaussian is both cheaper and easier to
 * reason about, and the "radius" slider now maps onto sigma directly instead of
 * through a platform-specific blur implementation.
 */

/** Radius slider value -> sigma, matching the tool's previous feel. */
export function radiusToSigma(radius: number, scaleFactor: number): number {
  return Math.max(0.6, radius * scaleFactor * 0.4);
}

/** Separable Gaussian blur with clamped edges. Returns a new buffer. */
export function gaussianBlur(
  src: Uint8ClampedArray,
  width: number,
  height: number,
  sigma: number
): Uint8ClampedArray {
  if (sigma <= 0.01) return src.slice();

  const kernel = buildKernel(sigma);
  const radius = (kernel.length - 1) / 2;
  const tmp = new Uint8ClampedArray(src.length);
  const out = new Uint8ClampedArray(src.length);

  // Horizontal pass. `tmp` is writable so the input buffer is never modified.
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let k = -radius; k <= radius; k++) {
        const sx = clamp(x + k, width);
        const s = (row + sx) << 2;
        const w = kernel[k + radius];
        r += src[s] * w;
        g += src[s + 1] * w;
        b += src[s + 2] * w;
        a += src[s + 3] * w;
      }
      const d = (row + x) << 2;
      tmp[d] = r;
      tmp[d + 1] = g;
      tmp[d + 2] = b;
      tmp[d + 3] = a;
    }
  }

  // Vertical pass, reading from `tmp` and writing to `out`.
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let k = -radius; k <= radius; k++) {
        const sy = clamp(y + k, height);
        const s = (sy * width + x) << 2;
        const w = kernel[k + radius];
        r += tmp[s] * w;
        g += tmp[s + 1] * w;
        b += tmp[s + 2] * w;
        a += tmp[s + 3] * w;
      }
      const d = (y * width + x) << 2;
      out[d] = r;
      out[d + 1] = g;
      out[d + 2] = b;
      out[d + 3] = a;
    }
  }

  return out;
}

function clamp(value: number, limit: number): number {
  if (value < 0) return 0;
  if (value >= limit) return limit - 1;
  return value;
}

/** Normalised 1-D Gaussian kernel, truncated at ~3 sigma. */
function buildKernel(sigma: number): Float32Array {
  const radius = Math.max(1, Math.ceil(sigma * 3));
  const size = radius * 2 + 1;
  const kernel = new Float32Array(size);
  const denom = 2 * sigma * sigma;
  let sum = 0;
  for (let i = -radius; i <= radius; i++) {
    const value = Math.exp(-(i * i) / denom);
    kernel[i + radius] = value;
    sum += value;
  }
  for (let i = 0; i < size; i++) kernel[i] /= sum;
  return kernel;
}

/**
 * Unsharp mask: sharpened = original + amount * (original - blurred).
 *
 * Returns a new buffer; `src` is left untouched. Alpha is passed through
 * unchanged, because sharpening an alpha channel would fringe transparent
 * regions against their opaque neighbours.
 */
export function unsharpMask(
  src: Uint8ClampedArray,
  width: number,
  height: number,
  amount: number,
  sigma: number,
  onProgress?: (phase: 'blur' | 'combine') => void
): Uint8ClampedArray {
  if (amount <= 0) return src.slice();

  onProgress?.('blur');
  const blurred = gaussianBlur(src, width, height, sigma);

  onProgress?.('combine');
  const out = new Uint8ClampedArray(src.length);
  for (let i = 0; i < src.length; i += 4) {
    out[i] = src[i] + amount * (src[i] - blurred[i]);
    out[i + 1] = src[i + 1] + amount * (src[i + 1] - blurred[i + 1]);
    out[i + 2] = src[i + 2] + amount * (src[i + 2] - blurred[i + 2]);
    out[i + 3] = src[i + 3];
  }
  return out;
}